import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

import { CHUNK_SIZE_M } from "../simulation/constants";
import { sampleHeight } from "../world/terrain";
import type { ChunkData } from "../world/chunks";
import { createConcreteTexture, createMetalPbrMaps, type MetalPbrMaps } from "./textures";

const MAX_INSTANCES = 256;

type VariantMesh = {
  mesh: THREE.InstancedMesh;
  dummy: THREE.Object3D;
};

/**
 * Instanced low-poly industrial buildings synced from seeded chunk data.
 * Keeps draw calls low while filling the tangent patch with readable props.
 */
export class BuildingField {
  private readonly group = new THREE.Group();
  private readonly variants: VariantMesh[];
  private readonly metal: MetalPbrMaps;
  private readonly concrete: THREE.CanvasTexture;
  /** Same array reference from ChunkStreamer while the neighborhood is stable. */
  private lastChunks: readonly ChunkData[] | null = null;

  public constructor(scene: THREE.Scene) {
    this.metal = createMetalPbrMaps(0x5a6066);
    this.concrete = createConcreteTexture(0x3a3834);
    this.variants = [
      this.makeMetalVariant(createTowerGeometry(), 0x6a7078),
      this.makeConcreteVariant(createBunkerGeometry(), 0x4a4640),
      this.makeMetalVariant(createSiloGeometry(), 0x5a5248),
      this.makeMetalVariant(createGantryGeometry(), 0x6a6258),
      this.makeConcreteVariant(createTankGeometry(), 0x4e4a42),
      this.makeMetalVariant(createWreckGeometry(), 0x6a5040),
      this.makeMetalVariant(createDebrisGeometry(), 0x4a4440),
      this.makeFoliageVariant(createDeadTreeGeometry(), 0x6d5136),
      this.makeFoliageVariant(createConiferGeometry(), 0x3e713b),
    ];
    for (const v of this.variants) this.group.add(v.mesh);
    scene.add(this.group);
  }

  public sync(chunks: readonly ChunkData[], seed: number): void {
    if (chunks === this.lastChunks) return;
    this.lastChunks = chunks;

    for (let v = 0; v < this.variants.length; v += 1) {
      const variant = this.variants[v]!;
      let count = 0;
      for (const chunk of chunks) {
        const ox = chunk.cx * CHUNK_SIZE_M;
        const oz = chunk.cy * CHUNK_SIZE_M;
        for (const b of chunk.buildings) {
          if (b.variant !== v || count >= MAX_INSTANCES) continue;
          const wx = ox + b.localX;
          const wz = oz + b.localY;
          variant.dummy.position.set(wx, sampleHeight(seed, wx, wz), wz);
          variant.dummy.rotation.set(0, -b.yaw, 0);
          variant.dummy.scale.setScalar(b.scale);
          variant.dummy.updateMatrix();
          variant.mesh.setMatrixAt(count, variant.dummy.matrix);
          count += 1;
        }
      }
      variant.mesh.count = count;
      variant.mesh.instanceMatrix.needsUpdate = true;
      variant.mesh.computeBoundingSphere();
    }
  }

  public dispose(): void {
    for (const v of this.variants) {
      v.mesh.geometry.dispose();
      const mat = v.mesh.material as THREE.MeshStandardMaterial;
      mat.map = null;
      mat.dispose();
    }
    this.metal.map.dispose();
    this.metal.roughnessMap.dispose();
    this.metal.metalnessMap.dispose();
    this.concrete.dispose();
    this.group.removeFromParent();
  }

  private makeMetalVariant(geometry: THREE.BufferGeometry, color: number): VariantMesh {
    const mat = new THREE.MeshStandardMaterial({
      color,
      map: this.metal.map,
      roughnessMap: this.metal.roughnessMap,
      metalnessMap: this.metal.metalnessMap,
      metalness: 0.68,
      roughness: 0.58,
      envMapIntensity: 0.32,
      emissive: color,
      emissiveIntensity: 0.12,
    });
    return this.createVariant(geometry, mat);
  }

  private makeConcreteVariant(geometry: THREE.BufferGeometry, color: number): VariantMesh {
    const mat = new THREE.MeshStandardMaterial({
      color,
      map: this.concrete,
      metalness: 0.16,
      roughness: 0.78,
      envMapIntensity: 0.08,
      emissive: color,
      emissiveIntensity: 0.1,
    });
    return this.createVariant(geometry, mat);
  }

  private makeFoliageVariant(geometry: THREE.BufferGeometry, color: number): VariantMesh {
    // Organic shapes should not inherit the concrete noise map: it made every
    // tree look like a charcoal-black industrial spike at game distance.
    const mat = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.94,
      metalness: 0,
      emissive: 0x0a1508,
      emissiveIntensity: 0.18,
    });
    return this.createVariant(geometry, mat);
  }

  private createVariant(
    geometry: THREE.BufferGeometry,
    mat: THREE.MeshStandardMaterial,
  ): VariantMesh {
    const mesh = new THREE.InstancedMesh(geometry, mat, MAX_INSTANCES);
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.count = 0;
    mesh.frustumCulled = true;
    return { mesh, dummy: new THREE.Object3D() };
  }
}

function createTowerGeometry(): THREE.BufferGeometry {
  const shaft = new THREE.BoxGeometry(4.4, 8.4, 4.4);
  shaft.translate(0, 4.2, 0);
  const cap = new THREE.BoxGeometry(5.3, 1.1, 5.3);
  cap.translate(0, 8.8, 0);
  const mast = new THREE.CylinderGeometry(0.18, 0.22, 2.1, 6);
  mast.translate(0, 10.4, 0);
  return mergeGeometries([shaft, cap, mast], false)!;
}

function createBunkerGeometry(): THREE.BufferGeometry {
  const body = new THREE.BoxGeometry(8.5, 4.2, 6.2);
  body.translate(0, 2.1, 0);
  const ramp = new THREE.BoxGeometry(3.2, 0.45, 2.4);
  ramp.translate(0, 0.28, 3.8);
  const slit = new THREE.BoxGeometry(5.4, 0.35, 0.4);
  slit.translate(0, 3.4, 3.0);
  return mergeGeometries([body, ramp, slit], false)!;
}

function createSiloGeometry(): THREE.BufferGeometry {
  const tank = new THREE.CylinderGeometry(2.2, 2.6, 9, 8);
  tank.translate(0, 4.5, 0);
  const dome = new THREE.SphereGeometry(2.15, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2);
  dome.translate(0, 9, 0);
  const pipe = new THREE.CylinderGeometry(0.28, 0.28, 4.2, 6);
  pipe.rotateZ(Math.PI / 2);
  pipe.translate(3.1, 3.6, 0);
  return mergeGeometries([tank, dome, pipe], false)!;
}

function createGantryGeometry(): THREE.BufferGeometry {
  const deck = new THREE.BoxGeometry(11, 0.42, 2.6);
  deck.translate(0, 7.4, 0);
  const legL = new THREE.BoxGeometry(0.55, 7.4, 0.55);
  legL.translate(-4.8, 3.7, 0.8);
  const legR = new THREE.BoxGeometry(0.55, 7.4, 0.55);
  legR.translate(4.8, 3.7, -0.8);
  const brace = new THREE.BoxGeometry(9.6, 0.28, 0.28);
  brace.translate(0, 4.6, 0);
  return mergeGeometries([deck, legL, legR, brace], false)!;
}

function createTankGeometry(): THREE.BufferGeometry {
  const drum = new THREE.CylinderGeometry(2.5, 2.5, 5.4, 10);
  drum.translate(0, 2.7, 0);
  const cap = new THREE.SphereGeometry(2.5, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
  cap.translate(0, 5.4, 0);
  const skirt = new THREE.CylinderGeometry(2.7, 2.9, 0.55, 10);
  skirt.translate(0, 0.28, 0);
  const pipe = new THREE.CylinderGeometry(0.26, 0.26, 3.4, 6);
  pipe.rotateZ(Math.PI / 2);
  pipe.translate(3.1, 3.2, 0);
  return mergeGeometries([drum, cap, skirt, pipe], false)!;
}

function mergeParts(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const prepared = parts.map((geo) => (geo.index ? geo.toNonIndexed() : geo));
  const merged = mergeGeometries(prepared, false);
  if (!merged) {
    throw new Error("prop merge failed");
  }
  return merged;
}

function createWreckGeometry(): THREE.BufferGeometry {
  const hull = new THREE.CapsuleGeometry(1.1, 6.4, 4, 8);
  hull.rotateZ(Math.PI / 2);
  hull.translate(0, 1.1, 0);
  const wing = new THREE.ConeGeometry(1.8, 0.28, 4);
  wing.rotateZ(0.4);
  wing.translate(-0.4, 1.35, 1.6);
  const tail = new THREE.ConeGeometry(0.7, 2.2, 6);
  tail.rotateZ(-1.1);
  tail.translate(3.4, 1.6, 0);
  return mergeParts([hull, wing, tail]);
}

function createDebrisGeometry(): THREE.BufferGeometry {
  const a = new THREE.BoxGeometry(2.4, 0.7, 1.3);
  a.translate(-0.6, 0.4, 0.3);
  const b = new THREE.CylinderGeometry(0.45, 0.55, 2.1, 6);
  b.rotateZ(1.1);
  b.translate(1.3, 0.55, -0.4);
  const c = new THREE.BoxGeometry(1.1, 0.8, 0.9);
  c.rotateZ(0.5);
  c.translate(0.2, 0.55, 1.1);
  return mergeParts([a, b, c]);
}

function createDeadTreeGeometry(): THREE.BufferGeometry {
  const trunk = new THREE.CylinderGeometry(0.22, 0.34, 4.8, 5);
  trunk.translate(0, 2.4, 0);
  const branchA = new THREE.CylinderGeometry(0.08, 0.18, 2.8, 5);
  branchA.rotateZ(-0.72);
  branchA.translate(0.7, 3.5, 0);
  const branchB = new THREE.CylinderGeometry(0.07, 0.15, 2.2, 5);
  branchB.rotateZ(0.88);
  branchB.translate(-0.58, 2.8, 0.15);
  return mergeParts([trunk, branchA, branchB]);
}

function createConiferGeometry(): THREE.BufferGeometry {
  const trunk = new THREE.CylinderGeometry(0.22, 0.32, 4.6, 6);
  trunk.translate(0, 2.3, 0);
  const low = new THREE.ConeGeometry(2.1, 3.4, 7);
  low.translate(0, 2.6, 0);
  const mid = new THREE.ConeGeometry(1.55, 3.2, 7);
  mid.translate(0, 4.35, 0);
  const top = new THREE.ConeGeometry(1.05, 2.9, 7);
  top.translate(0, 5.9, 0);
  return mergeParts([trunk, low, mid, top]);
}
